import type { ServerEvent, SessionInfo } from '@aw/contracts';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { PreviewStore, type ReplyPreview } from './previews';
import type { ConversationView } from './types';
import { ApiError, type StreamStatus, type WorkspaceApi } from './WorkspaceApi';

/** Fired on window when any request finds the session gone; the app layout sends the person to sign in. */
export const SESSION_ENDED = 'aw:session-ended';

function signalIfSignedOut(error: unknown) {
  if (error instanceof ApiError && error.code === 'unauthorized' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SESSION_ENDED));
  }
}

const StreamContext = createContext<StreamStatus | null>(null);

/** The live-updates connection, or null on the mock (it has no network to lose). */
export function useStreamStatus(): StreamStatus | null {
  return useContext(StreamContext);
}

const PreviewContext = createContext<PreviewStore | null>(null);
const NO_PREVIEWS: ReplyPreview[] = [];

/** Replies typing in for one conversation (never stored; see PreviewStore). */
export function useReplyPreviews(conversationId: string | null): ReplyPreview[] {
  const store = useContext(PreviewContext);
  return useSyncExternalStore(store?.subscribe ?? (() => () => {}), () =>
    store && conversationId ? store.forConversation(conversationId) : NO_PREVIEWS,
  );
}

const ApiContext = createContext<WorkspaceApi | null>(null);

export function useApi(): WorkspaceApi {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi outside ApiProvider');
  return api;
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: signalIfSignedOut }),
    mutationCache: new MutationCache({ onError: signalIfSignedOut }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        // Client errors are answers, not glitches; only offline and server errors retry.
        retry: (count, error) =>
          !(error instanceof ApiError && error.status > 0 && error.status < 500) && count < 2,
      },
      mutations: { retry: false },
    },
  });
}

/** Which cached views each server event makes stale. Only mounted queries refetch. */
export function keysFor(event: ServerEvent): unknown[][] {
  switch (event.type) {
    case 'run.updated':
      return [
        ['run', event.run.id],
        ['home'],
        ['conversations'],
        ['year'],
        ['messages', event.run.conversationId ?? '-'],
        ['usageMe'],
        ['session'],
      ];
    case 'document.updated':
      return [['document', event.document.id], ['home'], ['year'], ['clients'], ['needs']];
    case 'question.opened':
    case 'question.answered':
      return [
        ['question', event.question.id],
        ['questions'],
        ['home'],
        ['clients'],
        ['year'],
        ['clientFile'],
        ['conversations'],
        ['document', event.question.documentId],
      ];
    case 'message.created':
      return [
        ['messages', event.message.conversationId],
        ['conversations'],
        ['conversation', event.message.conversationId],
      ];
    case 'conversation.deleted':
      return [['conversations'], ['conversation', event.conversationId]];
    case 'needs.updated':
      return [['needs', event.taxYearId], ['year'], ['home'], ['clients'], ['clientFile']];
    case 'deliverable.updated':
      return [
        ['deliverable', event.deliverable.id],
        ['year'],
        ['home'],
        ['clients'],
        ['clientFile'],
      ];
    case 'tax_year.locked':
      return [['year'], ['years'], ['clientFile'], ['clients'], ['deliverable']];
    case 'budget.updated':
      return [
        ['usageMe'],
        ['usageFirm'],
        ['session'],
        ['home'],
        ['users'],
        ['audit'],
        ['allowanceRequests'],
      ];
    default:
      return [];
  }
}

/** A tab hidden this long reloads its views when it comes back. */
const AWAY_MS = 10 * 60_000;
const TICK_MS = 30_000;
/** a gap this long between ticks in a visible tab: the machine was asleep */
const SLEPT_MS = 2 * 60_000;

function EventBridge({
  api,
  previews,
  onStatus,
}: {
  api: WorkspaceApi;
  previews: PreviewStore;
  onStatus: (s: StreamStatus) => void;
}) {
  const qc = useQueryClient();
  // The stream opens only for a signed-in person and reopens when that person changes. The
  // session is read from the cache itself (not an observer), because sign-in and sign-out call
  // queryClient.clear(), which detaches observers.
  const [userId, setUserId] = useState<string | null>(
    () => qc.getQueryData<SessionInfo>(['session'])?.user.id ?? null,
  );
  useEffect(
    () =>
      qc.getQueryCache().subscribe(() => {
        const id = qc.getQueryData<SessionInfo>(['session'])?.user.id ?? null;
        setUserId((prev) => (prev === id ? prev : id));
      }),
    [qc],
  );
  // Back from a long absence (a laptop asleep, a tab in the background): the stream may have died
  // without saying so, and the server keeps a day of events, so resuming can't cover the gap.
  // Every view reloads quietly and the stream opens again.
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    if (!userId) return;
    const resync = () => {
      void qc.invalidateQueries();
      setGeneration((g) => g + 1);
    };
    let lastTick = Date.now();
    let hiddenAt: number | null = document.hidden ? Date.now() : null;
    const tick = setInterval(() => {
      const now = Date.now();
      // timers stop while the machine sleeps; a long gap in a visible tab means it woke up
      if (!document.hidden && now - lastTick > SLEPT_MS) resync();
      lastTick = now;
    }, TICK_MS);
    const onVisibility = () => {
      if (document.hidden) hiddenAt = Date.now();
      else {
        if (hiddenAt !== null && Date.now() - hiddenAt > AWAY_MS) resync();
        hiddenAt = null;
        lastTick = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [qc, userId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: generation reopens the stream after a resync
  useEffect(() => {
    previews.clear();
    if (!userId) return;
    let dropped = false;
    return api.subscribe(
      (event) => {
        previews.handle(event);
        if (event.type === 'run.updated') qc.setQueryData(['run', event.run.id], event.run);
        if (event.type === 'document.updated')
          qc.setQueryData(['document', event.document.id], event.document);
        // An empty conversation the sweep removed leaves the lists at once.
        if (event.type === 'conversation.deleted') {
          const gone = event.conversationId;
          qc.setQueryData<ConversationView[]>(['conversations'], (list) =>
            list?.filter((c) => c.id !== gone),
          );
          qc.removeQueries({ queryKey: ['messages', gone] });
        }
        for (const key of keysFor(event)) void qc.invalidateQueries({ queryKey: key });
      },
      (status) => {
        onStatus(status);
        if (status === 'reconnecting') dropped = true;
        // Back after a drop: the stream replays from Last-Event-ID, but views loaded meanwhile may be stale.
        if (status === 'live' && dropped) {
          dropped = false;
          void qc.invalidateQueries();
        }
        if (status === 'ended') window.dispatchEvent(new Event(SESSION_ENDED));
      },
      // the server couldn't catch this browser up: every view reloads, quietly
      () => void qc.invalidateQueries(),
    );
  }, [api, qc, onStatus, userId, previews, generation]);
  return null;
}

export function ApiProvider({
  api,
  client,
  children,
}: {
  api: WorkspaceApi;
  client?: QueryClient;
  children: ReactNode;
}) {
  const [qc] = useState(() => client ?? makeQueryClient());
  const [stream, setStream] = useState<StreamStatus | null>(null);
  const [previews] = useState(() => new PreviewStore());
  return (
    <ApiContext.Provider value={api}>
      <QueryClientProvider client={qc}>
        <StreamContext.Provider value={api.mode === 'http' ? stream : null}>
          <PreviewContext.Provider value={previews}>
            <EventBridge api={api} previews={previews} onStatus={setStream} />
            {children}
          </PreviewContext.Provider>
        </StreamContext.Provider>
      </QueryClientProvider>
    </ApiContext.Provider>
  );
}
